import { useParams, Link, useLocation } from "wouter";
import {
  useGetMembersContent,
  useGetCreator,
  useListMembersContent,
  useLikeMembersContent,
  useRecordMembersContentView,
} from "@workspace/api-client-react";
import type { MembersContent, Creator } from "@workspace/api-client-react";
import {
  ArrowLeft, Eye, Heart, Share2, Zap, ExternalLink,
  Play, Layers, Box, Clock, BadgeCheck, ShieldAlert,
  Download, Bookmark, MoreHorizontal, ChevronLeft, ChevronRight,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { useToast } from "@/hooks/use-toast";
import { ContentOrientationToggle, type ContentOrientation } from "@/components/ContentOrientationToggle";
import { AdultLockedLink, AdultLockOverlay } from "@/components/AdultLockedLink";
import { ComicBooklet } from "@/components/ComicBooklet";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api";
import { getIdToken } from "@/lib/firebase";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";

const BALCONY_PREVIEW_PAGES = Array.from({ length: 6 }, (_, index) => ({
  src: `/balcony-between-worlds/preview/page-${String(index + 1).padStart(2, "0")}.png`,
  alt: `Balcony Between Worlds preview page ${index + 1} of 6`,
}));

const CLAUDIA_ATTRIBUTION = "Aesthetics Inspired by Artist @JoeRusSketchBook IG";
const CLAUDIA_ATTRIBUTION_URL = "https://www.instagram.com/joerussketchbook?igsi=dWFzNG11ZzZnYzJu";

function PostDescription({ post }: { post: MembersContent }) {
  if (post.id !== 117 || !post.description?.includes(CLAUDIA_ATTRIBUTION)) {
    return <>{post.description}</>;
  }

  const [before, after = ""] = post.description.split(CLAUDIA_ATTRIBUTION);
  return (
    <>
      {before}
      <a
        href={CLAUDIA_ATTRIBUTION_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2 hover:text-primary/80"
      >
        {CLAUDIA_ATTRIBUTION}
      </a>
      {after}
    </>
  );
}

// Static per-post image galleries (content ID → ordered image array)
const POST_GALLERIES: Record<number, { src: string; caption?: string }[]> = {
  // Angel's of ISM — Issue #1 "Balcony Between Worlds"
  41: [
    { src: "/aoi-issue1-cover-rift-guardians.jpeg", caption: "Panel 1/24 — The Time Rift Guardians assemble" },
    { src: "/aoi-issue1-miami-street-fight.webp", caption: "ISM City Miami — shadow wraiths close in" },
    { src: "/aoi-issue1-miami-neon-duo.webp", caption: "South Beach — the rift crew holds the block" },
    { src: "/aoi-issue1-panels-introductions.webp", caption: "\"Guess introductions are over.\"" },
    { src: "/aoi-issue1-beach-werewolf.webp", caption: "The beach confrontation — wolf awakened" },
    { src: "/aoi-issue1-studio-costume-a.webp", caption: "Pre-shoot — Emerald Onyx suit-up" },
    { src: "/aoi-issue1-studio-costume-b.webp", caption: "Character ref — Emerald Onyx (variant)" },
  ],
  // Nexus Point: TRG Episode 3 Teaser
  44: [
    { src: "/trg-ep3-nexus-command-table.jpeg", caption: "The Nexus Event — TRG command station briefing" },
    { src: "/trg-ep3-nexus-emerald-ism.jpeg", caption: "Emerald & ISM — something's wrong with the readings" },
    { src: "/trg-ep3-nexus-ism-command.jpeg", caption: "ISM takes the platform — the team holds position" },
    { src: "/trg-ep3-nexus-map-restored.jpeg", caption: "Half the city restored. Half turned to ash." },
    { src: "/trg-ep3-nexus-redhead.jpeg", caption: "She knows more than she's saying" },
  ],
  // BTS w/ AV — Ep. 3 "Prep Before the Storm"
  106: [
    { src: "/bts-av-ep3-ill95-backpack.jpeg", caption: "ILL-95 handoff — the bag is packed" },
    { src: "/bts-av-ep3-av-ak-talk.jpeg", caption: "AV & AK — finalizing the Miami plan" },
    { src: "/bts-av-ep3-planning-board.jpeg", caption: "The board doesn't lie — every face accounted for" },
    { src: "/bts-av-ep3-infiltrators-miami.jpeg", caption: "The Infiltrators — they're already in Miami" },
  ],
  // Storm in the Horizon w/ A.V.
  115: [
    { src: "/av-storm-horizon-cover.jpeg", caption: "The horizon turns — A.V. watches the storm roll over the city" },
    { src: "/av-storm-horizon-still-1.jpeg", caption: "The rooftop threat closes in" },
    { src: "/av-storm-horizon-still-2.jpeg", caption: "A.V. turns to face the shadow" },
    { src: "/av-storm-horizon-still-3.jpeg", caption: "The escape begins above the city" },
    { src: "/av-storm-horizon-still-4.jpeg", caption: "Street-level backup arrives" },
    { src: "/av-storm-horizon-still-5.jpeg", caption: "The horizon holds its line" },
  ],
  // Arrival of the Storm ... w/ A.V. — Emerald Onyx
  116: [
    { src: "/emerald-arrival-storm-cover.jpeg", caption: "The storm arrives — Emerald Onyx and A.V. face the night" },
    { src: "/emerald-arrival-storm-still-1.jpeg", caption: "The rooftop alliance takes position" },
    { src: "/emerald-arrival-storm-still-2.jpeg", caption: "A.V. steps into the storm" },
    { src: "/emerald-arrival-storm-still-3.jpeg", caption: "The city watches from behind them" },
    { src: "/emerald-arrival-storm-still-4.jpeg", caption: "Emerald and A.V. hit Miami" },
    { src: "/emerald-arrival-storm-still-5.jpeg", caption: "The arrival becomes a showdown" },
  ],
  // Claudia G. — Training Day's w/ Claudia (TTTA Series)
  117: [
    { src: "/claudia-ttta-training-day-cover.png", caption: "Training day — Claudia G. and AK for The Time Travel Agent series" },
    { src: "/claudia-ak-only-memberz-still-1.png", caption: "The handoff — Claudia G. and AK connect in the locker room" },
    { src: "/claudia-ak-only-memberz-still-2.png", caption: "AK presents the Luxe VIP package" },
    { src: "/claudia-ak-only-memberz-still-3.png", caption: "Only Memberz black hoodie feature — production still" },
    { src: "/claudia-ak-only-memberz-still-4.png", caption: "Claudia G. — Only Memberz locker room portrait" },
  ],
  // Storm Warning's w/ CG — The Time Traveling Agent
  129: [
    { src: "/cgls-storm-warning-cover.png", caption: "The Time Traveling Agent — Storm Warning's w/ CG" },
    { src: "/cgls-storm-warning-claudia.png", caption: "Claudia G. receives the warning before the storm arrives" },
    { src: "/cgls-storm-warning-handoff.png", caption: "AK-Shots equips Claudia with a tool to see through the storm" },
    { src: "/cgls-storm-warning-glasses.png", caption: "The tool activates — Claudia can see through the storm" },
    { src: "/cgls-storm-warning-title.png", caption: "The Time Traveling Agent · Storm Warning's w/ CG" },
  ],
  // Storm Chasing w/ A.V. — Behind the scenes
  130: [
    { src: "/av-storm-chasing-cover.jpeg", caption: "A.V. alerts the team that the storm is coming" },
    { src: "/av-storm-chasing-still-1.jpeg", caption: "A.V. takes the wheel and starts the storm-chasing run" },
    { src: "/av-storm-chasing-still-2.jpeg", caption: "The storm-chasing mission arrives in the city" },
    { src: "/av-storm-chasing-still-3.jpeg", caption: "A.V. brings the team inside to prepare" },
    { src: "/av-storm-chasing-still-4.jpeg", caption: "AK receives the tools for the mission" },
    { src: "/av-storm-chasing-still-5.jpeg", caption: "The team gets the needed packs together" },
    { src: "/av-storm-chasing-still-6.jpeg", caption: "A.V. briefs the crew before they head back into the storm" },
    { src: "/av-storm-chasing-still-7.jpeg", caption: "The equipment is ready for the storm" },
    { src: "/av-storm-chasing-still-8.jpeg", caption: "Final preparations before storm chasing begins" },
  ],
  // The Love at Last Show — Lindsey Renee and Friends
  118: [
    { src: "/lr-love-at-last-cover.jpg", caption: "The Love at Last Show — Detroit's Eastside Market" },
    { src: "/lr-day-in-my-life.jpg", caption: "Lindsey Renee — day in my life" },
    { src: "/lr-solo-records.webp", caption: "Lindsey Renee at Solo Records" },
    { src: "/lr-rhythm-soul-patrol-logo.webp", caption: "Lindsey Renee's Rhythm & Soul Patrol" },
    { src: "/lr-love-at-last-portrait-1.jpg", caption: "Lindsey Renee — Love At Last portrait" },
    { src: "/lr-love-at-last-portrait-2.jpg", caption: "Lindsey Renee — Love At Last portrait" },
  ],
  // On The Scene w/ Lexi — Circle Of Influence / Ref's Of ILL / Blood Eclipse
  120: [
    { src: "/lexi-on-the-scene-cover.png", caption: "On The Scene w/ Lexi — Circle Of Influence episode teaser" },
    { src: "/lexi-on-the-scene-portrait.png", caption: "Lexi Black — Detroit music artist and model/actress" },
    { src: "/lexi-on-the-scene-studio-bag.png", caption: "The VIP handoff — On The Scene w/ Lexi" },
    { src: "/lexi-on-the-scene-studio-handoff.png", caption: "In the studio — Lexi Black and Illusionistz" },
    { src: "/lexi-on-the-scene-stage.png", caption: "Live performance moment — Circle Of Influence" },
    { src: "/lexi-on-the-scene-bts.png", caption: "Behind the scenes — On The Scene w/ Lexi" },
  ],
  // Starr Gazing ... w/ Starlight21 — Behind-The-Scenes "In the Mixx"
  121: [
    { src: "/starr-gazing-teaser-cover.png", caption: "Starr Gazing ... — episode teaser title frame" },
    { src: "/starr-gazing-still-1.png", caption: "Starlight21 — Behind-The-Scenes In the Mixx" },
    { src: "/starr-gazing-still-2.png", caption: "Private performance — Dallas birthday party" },
    { src: "/starr-gazing-still-3.png", caption: "In the Mixx — Starr Gazing performance moment" },
    { src: "/starr-gazing-still-4.png", caption: "Starlight21 — Dallas private performance" },
    { src: "/starr-gazing-still-5.png", caption: "Behind the scenes — Starr Gazing ..." },
  ],
  // Joe Rus Sketch Book — Jean Grey / The Phoenix collection
  122: [
    { src: "/jr-jean-phoenix-fire.jpg", caption: "Jean Grey / The Phoenix — fire and bird insignia" },
    { src: "/jr-jean-phoenix-portrait.jpg", caption: "Jean Grey — golden flame portrait study" },
    { src: "/jr-jean-phoenix-gesture.jpg", caption: "Phoenix costume and gesture study" },
    { src: "/jr-jean-phoenix-look-4.webp", caption: "Red-and-yellow costume pass" },
    { src: "/jr-jean-phoenix-look-5.jpg", caption: "Flame-lit character study" },
    { src: "/jr-jean-phoenix-look-7.jpg", caption: "The Phoenix — finished linework" },
    { src: "/jr-jean-phoenix-look-8.jpg", caption: "Jean Grey — final sketchbook look" },
  ],
  // Joe Rus Sketch Book — staggered image gallery
  123: [
    { src: "/jr-jean-phoenix-portrait.jpg", caption: "Portrait study" },
    { src: "/jr-jean-phoenix-fire.jpg", caption: "Phoenix flame detail" },
    { src: "/jr-jean-phoenix-gesture.jpg", caption: "Golden costume detail" },
  ],
  // Joe Rus Sketch Book — staggered image gallery
  124: [
    { src: "/jr-jean-phoenix-look-4.webp", caption: "Gesture and silhouette pass" },
    { src: "/jr-jean-phoenix-look-5.jpg", caption: "Color and flame study" },
    { src: "/jr-jean-phoenix-look-7.jpg", caption: "Finished costume pass" },
    { src: "/jr-jean-phoenix-look-8.jpg", caption: "Sketchbook final" },
  ],
  // Joe Rus Sketch Book — Catwoman video production stills
  126: [
    { src: "/jr-catwoman-coloring.jpg", caption: "Catwoman — colored sticker study" },
    { src: "/jr-catwoman-sketch.jpg", caption: "Catwoman — sketchbook page" },
  ],
  // Joe Rus Sketch Book — staggered Catwoman image gallery
  127: [
    { src: "/jr-catwoman-coloring.jpg", caption: "Catwoman — ink and color" },
    { src: "/jr-catwoman-sketch.jpg", caption: "Catwoman — original sketch page" },
  ],
  // The Time Travel Agent w/ Claudia G. — Ep. 1 (Blood Eclipse Series Spin-Off)
  107: [
    { src: "/ttta-ep1-blood-eclipse-arrival.png", caption: "Thickness: Blood Eclipse — Claudia's Arrival · Spin-Off Issue #1" },
    { src: "/ttta-ep1-claudia-agent-desk.jpeg", caption: "Time agent on deck — the intelligence never sleeps" },
    { src: "/ttta-ep1-claudia-nightclub.jpg", caption: "Night scene — the contact is already in the room" },
    { src: "/ttta-ep1-claudia-poolside.jpg", caption: "Off mission — but never off the clock" },
    { src: "/ttta-ep1-claudia-4594.jpg", caption: "Claudia G. — back in the timeline" },
    { src: "/ttta-ep1-claudia-nightclub-portrait.png", caption: "On the scene — THICKNESS: Blood Eclipse · Spin-Off Issue #1" },
  ],
  // Aura Nova — Animated Counterpart Scene
  63: [
    { src: "/cgls-aura-nova-money.jpg", caption: "Aura Nova — the alter ego owns the room" },
    { src: "/cgls-aura-nova-nightclub.png", caption: "Night scene — Claudia G. in the pink" },
    { src: "/cgls-aura-nova-faceswap.jpg", caption: "AI character — Aura Nova render" },
    { src: "/cgls-aura-nova-tttm.jpg", caption: "TTTM character mock — Aura Nova in scene" },
    { src: "/cgls-aura-nova-pr-balcony.png", caption: "Puerto Rico — balcony session" },
    { src: "/cgls-aura-nova-event.png", caption: "Event — the scene is set" },
    { src: "/cgls-aura-nova-product.png", caption: "Product board — Claudia G. LifeStyle collection" },
    { src: "/cgls-aura-nova-shirt.jpg", caption: "Merch — the look on the street" },
  ],
  // Circle of Influence — On The Scene w/ Chave' | Episode 103
  111: [
    { src: "/ots-chave-ep103-cover.webp", caption: "Circle of Influence — On The Scene w/ Chave' Episode 103 · Title Card" },
    { src: "/ots-chave-ep103-still1.jpeg", caption: "Illusionistz & Chave' — the exchange at the bar" },
    { src: "/ots-chave-ep103-still2.jpeg", caption: "VIP bag drops — the circle delivers" },
    { src: "/ots-chave-ep103-still3.jpeg", caption: "The handoff — On The Scene, Episode 103" },
    { src: "/ots-chave-ep103-still4.jpeg", caption: "Chave' holds the scene — COI universe" },
  ],
  // On the Scene w/ Neek — Refs of ILL: Miami (COI Universe)
  108: [
    { src: "/ots-neek-miami-2.png", caption: "Refs of ILL: Miami — Neek on the scene" },
    { src: "/ots-neek-miami-3.png", caption: "Circle of Influence — the energy in the room" },
    { src: "/ots-neek-miami-4.png", caption: "On the scene — ILL-90 Ent. production" },
    { src: "/ots-neek-miami-5.png", caption: "Miami nightlife — COI universe" },
    { src: "/ots-neek-miami-6.png", caption: "The camera catches everything" },
    { src: "/ots-neek-miami-7.png", caption: "Refs of ILL: Miami — close up" },
    { src: "/ots-neek-miami-8.png", caption: "Scene closes — Neek wraps the shot" },
  ],
  // Circle of Influence — VIP w/ Birdy (Teaser)
  105: [
    { src: "/vip-birdy-penthouse-trio.jpeg", caption: "VIP suite — the circle tightens" },
    { src: "/vip-birdy-penthouse-couch.jpeg", caption: "After hours — Birdy & the crew, penthouse view" },
    { src: "/vip-birdy-studio-bag.jpeg", caption: "Production studio — ILL-95 handoff" },
    { src: "/vip-birdy-studio-birdy.jpeg", caption: "365 Sports set — Birdy on deck" },
    { src: "/vip-birdy-studio-anchor.jpeg", caption: "Anchor position — 365 Sports News" },
    { src: "/vip-birdy-street.jpeg", caption: "On the street — the look that started it all" },
  ],
  // The Masquerade Runner — Act One | Level 01: Influence (MistyHaze × Illusionistz)
  109: [
    { src: "/misty-masquerade-runner-cover.png", caption: "The Masquerade Runner — Misty Lane enters the ISM network w/ Cypher South Bend" },
    { src: "/misty-masquerade-runner-level01.png", caption: "Level 01: Influence — the title drops" },
    { src: "/misty-masquerade-runner-title.png", caption: "The Masquerade Runner · Act One" },
    { src: "/misty-masquerade-runner-dossier.png", caption: "Misty Lane — ILL-90 Event Support · Temporary Chicago Access · Model Runner" },
    { src: "/misty-masquerade-runner-street-close.png", caption: "On the block — masked and moving" },
    { src: "/misty-masquerade-runner-street-suv.png", caption: "Chicago night ops — the runner hits the street" },
    { src: "/misty-masquerade-runner-car-shadow.png", caption: "The shadow closes in — Act One ends here" },
  ],
  // Illusionistz collab mirror — same stills
  110: [
    { src: "/misty-masquerade-runner-cover.png", caption: "The Masquerade Runner — Misty Lane enters the ISM network w/ Cypher South Bend" },
    { src: "/misty-masquerade-runner-level01.png", caption: "Level 01: Influence — the title drops" },
    { src: "/misty-masquerade-runner-title.png", caption: "The Masquerade Runner · Act One" },
    { src: "/misty-masquerade-runner-dossier.png", caption: "Misty Lane — ILL-90 Event Support · Temporary Chicago Access · Model Runner" },
    { src: "/misty-masquerade-runner-street-close.png", caption: "On the block — masked and moving" },
    { src: "/misty-masquerade-runner-street-suv.png", caption: "Chicago night ops — the runner hits the street" },
    { src: "/misty-masquerade-runner-car-shadow.png", caption: "The shadow closes in — Act One ends here" },
  ],
  // Flawless Lounge — 2026 workshops and meet-ups
  131: [
    { src: "/flawless-facilitators-2026.jpg", caption: "Flawless Lounge Facilitators — 2026 courses and meet-up events" },
    { src: "/flawless-texture-curators-meetups.jpg", caption: "Flawless Texture Curators — Miami and Orlando meet-ups" },
    { src: "/flawless-holistic-haircare.jpg", caption: "Holistic haircare and smart business with Liz Marie" },
    { src: "/flawless-lounge-info-1.jpg", caption: "Flawless Lounge course and community information" },
    { src: "/flawless-lounge-info-2.jpg", caption: "Flawless Lounge program information" },
  ],
  // Flawless Lounge — BTS with A.V.
  132: [
    { src: "/flawless-bts-still-1.jpg", caption: "A.V. receives the package inside Flawless Lounge" },
    { src: "/flawless-bts-still-2.jpg", caption: "Liz Marie and A.V. — behind the scenes" },
    { src: "/flawless-bts-still-3.jpg", caption: "The conversation continues inside the lounge" },
    { src: "/flawless-bts-still-4.jpg", caption: "The story moves outside into the Miami night" },
  ],
  // The Crimson Sky Protocol — opening-page teaser
  133: Array.from({ length: 5 }, (_, index) => ({
    src: `/flawless-crimson-sky-page-${String(index + 1).padStart(2, "0")}.png`,
    caption: `The Crimson Sky Protocol — teaser page ${index + 1} of 5`,
  })),
  // Ref's of ILL: The Miami Convergence — opening-page teaser
  134: Array.from({ length: 5 }, (_, index) => ({
    src: `/flawless-miami-convergence-page-${String(index + 1).padStart(2, "0")}.png`,
    caption: `The Miami Convergence — teaser page ${index + 1} of 5`,
  })),
  // The Little Haiti Convergence — opening-page teaser
  135: Array.from({ length: 5 }, (_, index) => ({
    src: `/flawless-little-haiti-page-${String(index + 1).padStart(2, "0")}.png`,
    caption: `The Little Haiti Convergence — teaser page ${index + 1} of 5`,
  })),
  // Liz Marie × Bread Magazine
  136: [
    { src: "/bread-liz-marie-cover-salon.png", caption: "Bread Magazine — Flawless Beauty featuring Liz Marie" },
    { src: "/bread-liz-marie-cover-night.png", caption: "Flawless Beauty, Holistic Heart — Liz Marie" },
    { src: "/bread-liz-marie-feature.png", caption: "The Holistic Up — founder and visionary of Flawless Lounge" },
    { src: "/flawless-lounge-salon-feature.jpeg", caption: "Inside Flawless Lounge with Liz Marie" },
    { src: "/bread-butta-magazine-title.jpeg", caption: "Bread & Butta Magazine" },
  ],
};

const POST_EXTERNAL_LINKS: Record<number, { href: string; label: string }> = {
  136: {
    href: "https://breadandbuttamag.com/features/feat-liz-marie",
    label: "Read Liz Marie's Bread Magazine feature",
  },
};

const SLUG_TO_ID: Record<string, number> = {
  illusionistz: 7,
  emeraldonyx: 8,
  mistyhaze: 9,
  claudiag: 10,
  avtheentertainer: 11,
  germani: 12,
  mst: 13,
  goldie: 14,
  rhythmandsoulpatrol: 18,
  joerussketchbook: 19,
  flawlesslounge: 20,
};

// Reverse map: always use this for internal <Link> hrefs so that
// old URLs with social handles (e.g. /creators/claudiaglifestyle/post/107)
// still back-navigate to the correct route (/creators/claudiag).
const ID_TO_SLUG: Record<number, string> = {
  7: "illusionistz",
  8: "emeraldonyx",
  9: "mistyhaze",
  10: "claudiag",
  11: "avtheentertainer",
  12: "germani",
  13: "mst",
  14: "goldie",
  18: "rhythmandsoulpatrol",
  19: "joerussketchbook",
  20: "flawlesslounge",
};

function getYouTubeEmbedUrl(url: string | null | undefined): string | null {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");
    let videoId: string | null = null;

    if (host === "youtu.be") {
      videoId = parsed.pathname.split("/").filter(Boolean)[0] ?? null;
    } else if (host === "youtube.com" || host === "youtube-nocookie.com") {
      videoId = parsed.pathname === "/watch"
        ? parsed.searchParams.get("v")
        : parsed.pathname.match(/^\/(?:embed|shorts)\/([^/?#]+)/)?.[1] ?? null;
    }

    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  } catch {
    return null;
  }
}

const TIER_STYLES: Record<string, string> = {
  free: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
  standard: "bg-blue-500/20 text-blue-300 border border-blue-500/30",
  premium: "bg-violet-500/20 text-violet-300 border border-violet-500/30",
};

const TYPE_LABEL: Record<string, string> = {
  video: "Video", audio: "Audio", livestream: "Live",
  image: "Photo", photo: "Photo", series: "Series", virtual: "4D Space",
};

function fmtDuration(s: number) {
  const m = Math.floor(s / 60);
  const sec = String(s % 60).padStart(2, "0");
  return `${m}:${sec}`;
}

function fmtCount(n: number) {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

type SidebarProps = {
  post: MembersContent;
  creator?: Creator;
  slug: string;
  liked: boolean;
  likeCount: number;
  viewCount: number;
  copied: boolean;
  onLike: () => void;
  onShare: () => void;
};

function PostSidebar({ post, creator, slug, liked, likeCount, viewCount, copied, onLike, onShare }: SidebarProps) {
  return (
    <div className="flex flex-col gap-5">
      {/* Creator card */}
      {creator && (
        <Link href={`/creators/${slug}`} className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors group">
          <img
            src={creator.avatarUrl}
            alt={creator.displayName}
            className="w-10 h-10 rounded-full object-cover ring-2 ring-primary/30"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <p className="text-sm font-semibold text-white truncate">{creator.displayName}</p>
              {creator.verified && <BadgeCheck className="w-3.5 h-3.5 text-primary flex-shrink-0" />}
            </div>
            <p className="text-xs text-muted-foreground font-mono">@{creator.username}</p>
          </div>
          <ExternalLink className="w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
        </Link>
      )}

      {/* Post title + badges */}
      <div>
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wider ${TIER_STYLES[post.tier] ?? TIER_STYLES.free}`}>
            {post.tier}
          </span>
          <span className="text-[10px] font-mono text-primary border border-primary/30 px-2 py-0.5 rounded uppercase">
            {TYPE_LABEL[post.mediaType] ?? post.mediaType}
          </span>
          {post.isAdult && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/30 uppercase tracking-wider flex items-center gap-1">
              <ShieldAlert className="w-3 h-3" /> 18+
            </span>
          )}
        </div>
        <h1 className="text-lg font-bold text-white leading-snug">{post.title}</h1>
        {post.description && (
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed"><PostDescription post={post} /></p>
        )}
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-5 text-sm text-muted-foreground border-y border-border py-3">
        <span className="flex items-center gap-1.5">
          <Eye className="w-4 h-4" /> {fmtCount(viewCount)}
        </span>
        <span className="flex items-center gap-1.5">
          <Heart className="w-4 h-4" /> {fmtCount(likeCount)}
        </span>
        {post.durationSeconds != null && (
          <span className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" /> {fmtDuration(post.durationSeconds)}
          </span>
        )}
        <span className="flex items-center gap-1.5 ml-auto text-primary">
          <Zap className="w-4 h-4" /> {Number(post.gasFeeGlo).toFixed(5)} GLO
        </span>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={onLike}
          disabled={liked}
          className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${
            liked
              ? "bg-pink-500/20 border-pink-500/40 text-pink-400 cursor-default"
              : "border-border text-muted-foreground hover:bg-pink-500/10 hover:border-pink-500/30 hover:text-pink-400"
          }`}
        >
          <Heart className={`w-4 h-4 ${liked ? "fill-pink-400" : ""}`} />
          {liked ? "Liked" : "Like"}
        </button>

        <button
          onClick={onShare}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-border text-muted-foreground hover:bg-primary/10 hover:border-primary/30 hover:text-foreground transition-all"
        >
          <Share2 className="w-4 h-4" />
          {copied ? "Copied!" : "Share"}
        </button>

        <button className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-border text-muted-foreground hover:bg-card hover:text-foreground transition-all">
          <Bookmark className="w-4 h-4" />
          Save
        </button>

        <a
          href={`https://only-memberz.com/models/${creator?.username ?? slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-border text-muted-foreground hover:bg-card hover:text-foreground transition-all"
        >
          <ExternalLink className="w-4 h-4" />
          OM
        </a>
      </div>

      {/* Subscribe CTA */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
        <p className="text-xs text-muted-foreground mb-1">Subscribe to unlock all content</p>
        <p className="text-base font-bold text-white mb-3">
          {Number(creator?.subscriptionPriceHalo ?? 0).toFixed(4)}{" "}
          <span className="text-primary">HALO</span>
          <span className="text-xs font-normal text-muted-foreground"> / month</span>
        </p>
        <button className="w-full py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors">
          Subscribe on Only-Memberz
        </button>
      </div>

      {/* Gas fee note */}
      <p className="text-[10px] text-muted-foreground/50 text-center">
        GLO gas fee collected at upload · powered by ISM | New World
      </p>
    </div>
  );
}

export default function PostPage() {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const postId = Number(id);
  const creatorId = SLUG_TO_ID[slug ?? ""] ?? 0;
  const [relatedOrientation, setRelatedOrientation] = useState<ContentOrientation>("day");
  const [, setLocation] = useLocation();
  const { firebaseUser, loading: authLoading } = useAuth();
  const { startContentCheckout } = useStripeCheckout();
  const [comicAccess, setComicAccess] = useState({ hasAccess: false, isAdmin: false });
  const [accessLoading, setAccessLoading] = useState(false);
  const [fullIssueLoading, setFullIssueLoading] = useState(false);

  const { data: post, isLoading } = useGetMembersContent(postId);
  const { data: creator } = useGetCreator(post?.creatorId ?? creatorId);
  const { data: related } = useListMembersContent({ creatorId: post?.creatorId ?? creatorId, orientation: relatedOrientation });

  // Use the canonical route slug from the creator's ID so that old URLs with
  // social handles (e.g. /creators/claudiaglifestyle/post/107) still generate
  // correct back-links (/creators/claudiag) regardless of what's in the URL.
  const canonicalSlug = (post?.creatorId ? ID_TO_SLUG[post.creatorId] : undefined) ?? slug ?? "";

  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState<number | null>(null);
  const [viewCount, setViewCount] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [useWebmFallback, setUseWebmFallback] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const like = useLikeMembersContent();
  const recordView = useRecordMembersContentView();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (postId !== 128 || authLoading) return;
    if (!firebaseUser) {
      setComicAccess({ hasAccess: false, isAdmin: false });
      setAccessLoading(false);
      return;
    }

    let cancelled = false;
    setAccessLoading(true);
    apiFetch<{ hasAccess: boolean; isAdmin: boolean }>(
      "/api/comic-issues/balcony-between-worlds/access",
    )
      .then((access) => {
        if (!cancelled) setComicAccess(access);
      })
      .catch(() => {
        if (!cancelled) setComicAccess({ hasAccess: false, isAdmin: false });
      })
      .finally(() => {
        if (!cancelled) setAccessLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [postId, firebaseUser, authLoading]);

  // Record a view once when the post data first arrives, then sync caches
  useEffect(() => {
    if (!post) return;
    // Optimistically show count + 1 immediately so the user sees it tick up
    setViewCount((post.viewCount ?? 0) + 1);
    recordView.mutate(
      { id: post.id },
      {
        onSuccess: (d) => {
          // Confirm with the server's authoritative count
          setViewCount(d.viewCount);
          // Write into the detail cache so future visits see the correct value
          queryClient.setQueryData(
            [`/api/members/content/${post.id}`],
            (old: typeof post | undefined) =>
              old ? { ...old, viewCount: d.viewCount } : old,
          );
          // Also invalidate the list so gallery/feed cards stay in sync
          queryClient.invalidateQueries({ queryKey: ["/api/members/content"] });
          // GLO milestone: notify when the creator earns a view reward
          if (d.gloRewarded && d.gloRewarded > 0) {
            toast({
              title: `⚡ ${d.gloRewarded} GLO Earned`,
              description: `${post.creatorName} reached ${d.viewCount.toLocaleString()} views — milestone reward added to their profile.`,
              duration: 5000,
            });
          }
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.id]);

  useEffect(() => {
    setUseWebmFallback(false);
  }, [postId]);

  const currentLikeCount = likeCount ?? post?.likeCount ?? 0;
  const currentViewCount = viewCount ?? post?.viewCount ?? 0;
  const relatedPosts = related?.filter(r => r.id !== postId) ?? [];
  const currentIndex = relatedPosts.findIndex(r => r.id === postId);
  const allPosts = related ?? [];
  const thisIndex = allPosts.findIndex(r => r.id === postId);
  const prevPost = thisIndex > 0 ? allPosts[thisIndex - 1] : null;
  const nextPost = thisIndex >= 0 && thisIndex < allPosts.length - 1 ? allPosts[thisIndex + 1] : null;

  function handleLike() {
    if (liked || !post) return;
    like.mutate({ id: postId } as Parameters<typeof like.mutate>[0], {
      onSuccess: (d) => { setLiked(true); setLikeCount(d.likeCount); },
    });
  }

  function handleShare() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }

  function handleComicPackage(productId: 43 | 44) {
    if (!firebaseUser) {
      toast({
        title: "Sign in required",
        description: "Sign in to purchase this issue package and keep access on your account.",
      });
      setLocation("/auth");
      return;
    }
    startContentCheckout(productId);
  }

  async function handleOpenFullIssue() {
    setFullIssueLoading(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error("Sign in to open the full issue");
      const base = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
      const response = await fetch(
        `${base}/api/comic-issues/balcony-between-worlds/pdf`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error ?? "Unable to open the full issue");
      }
      const pdfUrl = URL.createObjectURL(await response.blob());
      const opened = window.open(pdfUrl, "_blank", "noopener,noreferrer");
      if (!opened) throw new Error("Allow pop-ups to open the full issue");
      window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
    } catch (err) {
      toast({
        title: "Issue unavailable",
        description: err instanceof Error ? err.message : "Unable to open the full issue",
        variant: "destructive",
      });
    } finally {
      setFullIssueLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-muted-foreground">
        <p>Post not found.</p>
        <Link href={`/creators/${canonicalSlug}`} className="text-primary hover:underline text-sm">
          ← Back to profile
        </Link>
      </div>
    );
  }

  // Series with a mediaUrl plays as video; without one it's a comic/image series
  const isVideo = (post.mediaType === "video") || (post.mediaType === "series" && !!post.mediaUrl);
  const isPhoto = post.mediaType === "photo" || post.mediaType === "image";
  const isComicSeries = post.mediaType === "series" && !post.mediaUrl;
  const youtubeEmbedUrl = getYouTubeEmbedUrl(post.mediaUrl);
  const playbackUrl = postId === 129
    ? "/CG_StormWarning1.mp4"
    : postId === 130
      ? "/AV_StormChasing.mp4"
      : post.mediaUrl;
  const playbackWebmUrl = postId === 129
    ? "/CG_StormWarning1.webm"
    : postId === 130
      ? "/AV_StormChasing.webm"
      : null;
  const activePlaybackUrl = useWebmFallback && playbackWebmUrl
    ? playbackWebmUrl
    : playbackUrl;

  const ogImage = post.thumbnailUrl?.startsWith("http")
    ? post.thumbnailUrl
    : `${window.location.origin}${post.thumbnailUrl}`;
  const ogTitle = `${post.title} — ISM | New World`;
  const ogDesc = `${post.creatorName} · ${post.category ?? "Content"} · Watch on ISM New World`;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDesc} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDesc} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDesc} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      {/* Sticky top bar */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4 max-w-7xl h-12 flex items-center gap-3">
          <Link
            href={`/creators/${canonicalSlug}`}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
            {creator?.displayName ?? "Profile"}
          </Link>
          <span className="text-border">·</span>
          <span className="text-sm text-white truncate font-medium">{post.title}</span>

          {/* Prev / Next */}
          <div className="ml-auto flex items-center gap-1">
            {prevPost ? (
              <Link
                href={`/creators/${canonicalSlug}/post/${prevPost.id}`}
                className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
                title={prevPost.title}
              >
                <ChevronLeft className="w-4 h-4" />
              </Link>
            ) : (
              <span className="p-1.5 rounded-lg border border-border text-border cursor-not-allowed">
                <ChevronLeft className="w-4 h-4" />
              </span>
            )}
            {nextPost ? (
              <Link
                href={`/creators/${canonicalSlug}/post/${nextPost.id}`}
                className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
                title={nextPost.title}
              >
                <ChevronRight className="w-4 h-4" />
              </Link>
            ) : (
              <span className="p-1.5 rounded-lg border border-border text-border cursor-not-allowed">
                <ChevronRight className="w-4 h-4" />
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl py-6">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* ── Media column ── */}
          <div className="flex-1 min-w-0">
            {/* Media card */}
            <div className={`${postId === 128 ? "" : "rounded-2xl overflow-hidden bg-black border border-border shadow-2xl"}`}>
              {postId === 128 ? (
                <ComicBooklet
                  title="Balcony Between Worlds · Issue #1"
                  pages={BALCONY_PREVIEW_PAGES}
                  fullIssueUnlocked={comicAccess.hasAccess}
                  accessLoading={authLoading || accessLoading}
                  isAdmin={comicAccess.isAdmin}
                  isAuthenticated={!!firebaseUser}
                  onUnlockPackage={handleComicPackage}
                  onOpenFullIssue={handleOpenFullIssue}
                  fullIssueLoading={fullIssueLoading}
                />
              ) : isVideo && post.mediaUrl ? (
                youtubeEmbedUrl ? (
                  <iframe
                    src={youtubeEmbedUrl}
                    title={post.title}
                    className="w-full aspect-video bg-black"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                ) : (
                  <video
                    key={`${postId}-${useWebmFallback ? "webm" : "mp4"}`}
                    ref={videoRef}
                    poster={post.thumbnailUrl ?? undefined}
                    controls
                    playsInline
                    className="w-full aspect-video object-contain bg-black"
                    preload="auto"
                    src={activePlaybackUrl ?? undefined}
                    onError={() => {
                      if (!useWebmFallback && playbackWebmUrl) {
                        setUseWebmFallback(true);
                      }
                    }}
                  >
                  </video>
                )
              ) : isComicSeries && post.thumbnailUrl ? (
                <img
                  src={post.thumbnailUrl}
                  alt={post.title}
                  className="w-full max-h-[85vh] object-contain bg-black"
                />
              ) : isPhoto ? (
                <img
                  src={post.thumbnailUrl ?? ""}
                  alt={post.title}
                  className="w-full max-h-[75vh] object-contain bg-black"
                />
              ) : post.mediaType === "virtual" ? (
                <div className="relative aspect-video flex flex-col items-center justify-center gap-3 bg-zinc-900 text-muted-foreground overflow-hidden">
                  {post.thumbnailUrl && (
                    <img src={post.thumbnailUrl} alt={post.title} className="absolute inset-0 w-full h-full object-cover opacity-40" />
                  )}
                  <div className="relative flex flex-col items-center gap-3">
                    <Box className="w-12 h-12 opacity-60" />
                    <p className="text-sm text-white font-medium">4D / Virtual Space</p>
                    {post.mediaUrl ? (
                      <a href={post.mediaUrl} target="_blank" rel="noopener noreferrer" className="px-5 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/80 transition-colors">
                        Enter 4D Space →
                      </a>
                    ) : (
                      <p className="text-xs text-muted-foreground/60">Enter from the 3D Viewer</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="aspect-video flex flex-col items-center justify-center gap-3 bg-zinc-900 text-muted-foreground">
                  <Layers className="w-12 h-12 opacity-30" />
                  <p className="text-sm capitalize">{post.mediaType}</p>
                </div>
              )}
            </div>

            {POST_EXTERNAL_LINKS[postId] && (
              <a
                href={POST_EXTERNAL_LINKS[postId].href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/20"
              >
                <span>{POST_EXTERNAL_LINKS[postId].label}</span>
                <ExternalLink className="h-4 w-4 flex-shrink-0" />
              </a>
            )}

            {/* Post image gallery (comic issues & promo posts) */}
            {POST_GALLERIES[postId] && (
              <div className="mt-6">
                <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                  {isComicSeries ? "Issue Pages" : "Production Stills"}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {POST_GALLERIES[postId].map((img, i) => (
                    <a
                      key={i}
                      href={img.src}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative block rounded-xl overflow-hidden bg-zinc-900 border border-border hover:border-primary/30 transition-all"
                    >
                      <img
                        src={img.src}
                        alt={img.caption ?? `Image ${i + 1}`}
                        className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {img.caption && (
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-[10px] text-white/90 leading-tight line-clamp-2">{img.caption}</p>
                        </div>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Mobile sidebar */}
            <div className="mt-5 lg:hidden">
              <PostSidebar
                post={post}
                creator={creator}
                slug={canonicalSlug}
                liked={liked}
                likeCount={currentLikeCount}
                viewCount={currentViewCount}
                copied={copied}
                onLike={handleLike}
                onShare={handleShare}
              />
            </div>

            {/* Related posts */}
            {related && (
              <div className="mt-8">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    More from {creator?.displayName}
                  </h3>
                  <ContentOrientationToggle value={relatedOrientation} onChange={setRelatedOrientation} />
                </div>
                {relatedPosts.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {relatedPosts.slice(0, 6).map(r => (
                    <AdultLockedLink
                      item={r}
                      href={`/creators/${canonicalSlug}/post/${r.id}`}
                      key={r.id}
                      className="group block rounded-xl overflow-hidden bg-card border border-border hover:border-primary/30 transition-all"
                    >
                      <div className="relative aspect-video overflow-hidden bg-zinc-900">
                        {r.thumbnailUrl && (
                          <img
                            src={r.thumbnailUrl}
                            alt={r.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                        {r.isAdult && <AdultLockOverlay />}
                        {(r.mediaType === "video" || r.mediaType === "series") && (
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                              <Play className="w-4 h-4 text-white fill-white ml-0.5" />
                            </div>
                          </div>
                        )}
                        <span className={`absolute top-1.5 left-1.5 text-[9px] font-semibold px-1.5 py-0.5 rounded uppercase ${TIER_STYLES[r.tier] ?? TIER_STYLES.free}`}>
                          {r.tier}
                        </span>
                      </div>
                      <div className="p-2.5">
                        <p className="text-xs font-medium text-white line-clamp-2 leading-snug">{r.title}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5 font-mono uppercase">{TYPE_LABEL[r.mediaType] ?? r.mediaType}</p>
                      </div>
                    </AdultLockedLink>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-border bg-card/40 py-8 text-center text-xs text-muted-foreground">
                    No {relatedOrientation.toUpperCase()} related posts are available.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Desktop sidebar ── */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-20">
              <PostSidebar
                post={post}
                creator={creator}
                slug={canonicalSlug}
                liked={liked}
                likeCount={currentLikeCount}
                viewCount={currentViewCount}
                copied={copied}
                onLike={handleLike}
                onShare={handleShare}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
